import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Stack } from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useAuth } from '../contexts/AuthContext';

const FONT = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

// Respect reduced-motion: strip transforms/transitions when requested.
const REDUCE = { '@media (prefers-reduced-motion: reduce)': { transition: 'none !important', transform: 'none !important', animation: 'none !important' } };

/** Two workspace panels, glassmorphism style. Presented after login for a
 *  dual-access user so the platform and business panels never mix. */
function PanelCard({ tone, Icon, title, desc, bullets, onEnter }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Enter ${title}`}
      onClick={onEnter}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEnter(); } }}
      sx={{
        position: 'relative', cursor: 'pointer', overflow: 'hidden',
        p: { xs: 2.5, md: 3 }, borderRadius: '20px', height: '100%',
        display: 'flex', flexDirection: 'column',
        background: 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
        border: '1px solid rgba(255,255,255,0.7)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 10px 30px rgba(15,23,42,0.06)',
        transition: 'transform .22s cubic-bezier(.16,.84,.44,1), box-shadow .22s, border-color .22s',
        '&:hover, &:focus-visible': {
          transform: 'translateY(-4px)',
          borderColor: tone.ring,
          boxShadow: `0 1px 0 rgba(255,255,255,0.7) inset, 0 22px 46px ${tone.glow}`,
        },
        '&:hover .enter, &:focus-visible .enter': { gap: '10px' },
        '&:hover .enter svg, &:focus-visible .enter svg': { transform: 'translateX(3px)' },
        '&:focus-visible': { outline: `3px solid ${tone.ring}`, outlineOffset: 3 },
        ...REDUCE,
      }}
    >
      {/* accent glow */}
      <Box aria-hidden sx={{ position: 'absolute', top: -70, right: -60, width: 180, height: 180, borderRadius: '50%', background: tone.blob, filter: 'blur(46px)', opacity: 0.5, pointerEvents: 'none' }} />

      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ width: 52, height: 52, borderRadius: '15px', display: 'grid', placeItems: 'center', color: '#fff', mb: 2, background: tone.iconBg, boxShadow: `0 8px 20px ${tone.glow}` }}>
          <Icon sx={{ fontSize: 27 }} />
        </Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '1.18rem', letterSpacing: '-0.01em', color: '#0F172A', mb: 0.5 }}>{title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: '0.86rem', color: '#64748B', lineHeight: 1.55, mb: 2 }}>{desc}</Typography>

        <Stack spacing={0.9} sx={{ mb: 2.5 }}>
          {bullets.map((b) => (
            <Stack key={b} direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 18, height: 18, borderRadius: '6px', display: 'grid', placeItems: 'center', flexShrink: 0, bgcolor: tone.soft, color: tone.fg }}>
                <CheckRoundedIcon sx={{ fontSize: 13 }} />
              </Box>
              <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: '#334155' }}>{b}</Typography>
            </Stack>
          ))}
        </Stack>

        <Box className="enter" sx={{ mt: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px', color: tone.fg, fontWeight: 800, fontFamily: FONT, fontSize: '0.9rem', transition: 'gap .2s', ...REDUCE }}>
          Enter
          <ArrowForwardRoundedIcon sx={{ fontSize: 19, transition: 'transform .2s', ...REDUCE }} />
        </Box>
      </Box>
    </Box>
  );
}

export default function PanelSelector() {
  const navigate = useNavigate();
  const { profile, logout } = useAuth();
  const [leaving, setLeaving] = useState(false);

  const choose = (mode, path) => {
    if (leaving) return;
    setLeaving(true);
    try { sessionStorage.setItem('panelMode', mode); } catch (e) { /* ignore */ }
    navigate(path, { replace: true });
  };

  const firstName = profile?.name ? profile.name.split(' ')[0] : '';

  const platform = {
    ring: 'rgba(99,102,241,0.55)', glow: 'rgba(79,70,229,0.28)', blob: 'radial-gradient(circle, #6366F1, transparent 68%)',
    iconBg: 'linear-gradient(135deg,#6366F1,#4F46E5)', soft: '#EEF2FF', fg: '#4338CA',
  };
  const business = {
    ring: 'rgba(255,102,0,0.55)', glow: 'rgba(234,88,12,0.28)', blob: 'radial-gradient(circle, #FF8A3D, transparent 68%)',
    iconBg: 'linear-gradient(135deg,#FF8A3D,#EA580C)', soft: '#FFF3EC', fg: '#C2410C',
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3 }, fontFamily: FONT, overflow: 'hidden', background: 'linear-gradient(160deg,#F7F8FC 0%,#EEF0F7 100%)' }}>
      {/* ambient light sources (static — no motion) */}
      <Box aria-hidden sx={{ position: 'absolute', top: '-12%', left: '-6%', width: 460, height: 460, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.22), transparent 66%)', filter: 'blur(30px)' }} />
      <Box aria-hidden sx={{ position: 'absolute', bottom: '-14%', right: '-8%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,138,61,0.20), transparent 66%)', filter: 'blur(30px)' }} />

      {/* glass shell */}
      <Box sx={{
        position: 'relative', width: '100%', maxWidth: 720, p: { xs: 2.5, sm: 4 }, borderRadius: '28px',
        background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)',
        border: '1px solid rgba(255,255,255,0.7)', boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 30px 80px rgba(15,23,42,0.14)',
      }}>
        {/* brand + welcome */}
        <Stack alignItems="center" spacing={1.25} sx={{ mb: { xs: 3, sm: 3.5 }, textAlign: 'center' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg,#FF8A3D,#EA580C)', boxShadow: '0 8px 20px rgba(234,88,12,0.3)' }}>
            <ShieldOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: { xs: '1.4rem', sm: '1.7rem' }, letterSpacing: '-0.02em', color: '#0F172A' }}>
            Welcome back{firstName ? `, ${firstName}` : ''}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.92rem', color: '#64748B', maxWidth: 420 }}>
            You have access to two workspaces. Choose where to continue — switch anytime from the account menu.
          </Typography>
        </Stack>

        {/* the two panels */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 1.75, sm: 2.25 } }}>
          <PanelCard
            tone={platform}
            Icon={AdminPanelSettingsOutlinedIcon}
            title="Platform Admin"
            desc="Run the platform across all tenants."
            bullets={['Tenants & billing', 'Platform admins', 'Site content']}
            onEnter={() => choose('platform', '/admin-panel')}
          />
          <PanelCard
            tone={business}
            Icon={BusinessOutlinedIcon}
            title="Business Account"
            desc="Operate your organization's consent workspace."
            bullets={['Purposes & notices', 'Consent & audit logs', 'Users & roles']}
            onEnter={() => choose('business', '/dashboard')}
          />
        </Box>

        {/* footer */}
        <Stack direction="row" alignItems="center" justifyContent="center" sx={{ mt: { xs: 2.5, sm: 3 }, pt: 2, borderTop: '1px solid rgba(15,23,42,0.06)' }}>
          <Button onClick={logout} startIcon={<LogoutRoundedIcon sx={{ fontSize: 17 }} />}
            sx={{ textTransform: 'none', fontFamily: FONT, fontWeight: 600, fontSize: '0.82rem', color: '#64748B', borderRadius: '10px', '&:hover': { color: '#0F172A', bgcolor: 'rgba(15,23,42,0.04)' }, ...REDUCE }}>
            Not you? Sign out
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
